/**
 * 枚举与中文显示名映射 — 与《数据库设计 v2.0》§1 一致：
 * 库里存 UPPER_SNAKE_CASE 明文字符串，中文显示名在前端映射（禁 int 编码）。
 */

export type Tone = 'ok' | 'warn' | 'dan' | 'pri' | 'mut' | 'vio';

export interface EnumMeta<T extends string> {
  label: string;
  tone?: Tone;
  /** 枚举码本身（UPPER_SNAKE_CASE），便于埋点与选项透传 */
  code?: T;
}

export const USER_TYPE = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  OPS_ADMIN: 'OPS_ADMIN',
  DATA_ANALYST: 'DATA_ANALYST',
  DISPATCH_SUPERVISOR: 'DISPATCH_SUPERVISOR',
  BPO_AGENT: 'BPO_AGENT',
  COURIER: 'COURIER',
} as const;
export type UserType = (typeof USER_TYPE)[keyof typeof USER_TYPE];

export const USER_TYPE_META: Record<UserType, EnumMeta<UserType>> = {
  SUPER_ADMIN: { label: '超级管理员' },
  OPS_ADMIN: { label: '运营管理员' },
  DATA_ANALYST: { label: '数据分析师' },
  DISPATCH_SUPERVISOR: { label: '交付机构管理员' },
  BPO_AGENT: { label: 'BPO客服', tone: 'warn' },
  COURIER: { label: '交付专员', tone: 'pri' },
};

export const USER_STATUS = {
  ACTIVE: 'ACTIVE',
  PENDING_AUDIT: 'PENDING_AUDIT',
  DISABLED: 'DISABLED',
  SUSPENDED: 'SUSPENDED',
} as const;
export type UserStatus = (typeof USER_STATUS)[keyof typeof USER_STATUS];

export const USER_STATUS_META: Record<UserStatus, EnumMeta<UserStatus>> = {
  ACTIVE: { label: '正常', tone: 'ok' },
  PENDING_AUDIT: { label: '待审核', tone: 'warn' },
  DISABLED: { label: '禁用', tone: 'dan' },
  SUSPENDED: { label: '停用', tone: 'mut' },
};

export const USER_END = { ADMIN: 'ADMIN', BPO: 'BPO', MINIAPP: 'MINIAPP' } as const;
export type UserEnd = (typeof USER_END)[keyof typeof USER_END];

export const USER_END_META: Record<UserEnd, EnumMeta<UserEnd>> = {
  ADMIN: { label: '管理端', tone: 'pri' },
  BPO: { label: '录单端', tone: 'warn' },
  MINIAPP: { label: '小程序', tone: 'vio' },
};

/** 端绑定互斥：外部角色仅绑专属端，禁止开通管理端 */
export const EXTERNAL_ENDS: readonly UserEnd[] = [USER_END.BPO, USER_END.MINIAPP];

export const COURIER_LEVEL = { GOLD: 'GOLD', SILVER: 'SILVER', BRONZE: 'BRONZE' } as const;
export type CourierLevel = (typeof COURIER_LEVEL)[keyof typeof COURIER_LEVEL];

export const COURIER_LEVEL_META: Record<CourierLevel, EnumMeta<CourierLevel>> = {
  GOLD: { label: '金牌', tone: 'warn' },
  SILVER: { label: '银牌', tone: 'pri' },
  BRONZE: { label: '铜牌', tone: 'mut' },
};

/** 交付员在岗状态只有两态（反馈_10 用户口径：请假已并入下线，后端不再下发 ON_LEAVE）。 */
export const COURIER_DUTY = { ONLINE: 'ONLINE', OFFLINE: 'OFFLINE' } as const;
export type CourierDuty = (typeof COURIER_DUTY)[keyof typeof COURIER_DUTY];

export const COURIER_DUTY_META: Record<CourierDuty, EnumMeta<CourierDuty>> = {
  ONLINE: { label: '接单中', tone: 'ok' },
  OFFLINE: { label: '已下线', tone: 'mut' },
};

export const CERT_STATUS = { CERTIFIED: 'CERTIFIED', PENDING: 'PENDING', MISSING: 'MISSING' } as const;
export type CertStatus = (typeof CERT_STATUS)[keyof typeof CERT_STATUS];

export const CERT_STATUS_META: Record<CertStatus, EnumMeta<CertStatus>> = {
  CERTIFIED: { label: '已认证', tone: 'ok' },
  PENDING: { label: '认证中', tone: 'warn' },
  MISSING: { label: '缺失', tone: 'dan' },
};

export const DATA_SCOPE = {
  ALL: 'ALL',
  OWN_REGION: 'OWN_REGION',
  SELF_ONLY: 'SELF_ONLY',
  SELF_ENTERED_ONLY: 'SELF_ENTERED_ONLY',
} as const;
export type DataScope = (typeof DATA_SCOPE)[keyof typeof DATA_SCOPE];

export const DATA_SCOPE_META: Record<DataScope, EnumMeta<DataScope>> = {
  ALL: { label: '全部' },
  OWN_REGION: { label: '本区域（所辖）' },
  SELF_ONLY: { label: '仅本人' },
  SELF_ENTERED_ONLY: { label: '仅本人录入' },
};

/** 订单状态（VARCHAR+CHECK，可扩展）。管理端页签按业务分组聚合多状态。 */
export const ORDER_STATUS = {
  PENDING_ASSIGN: 'PENDING_ASSIGN',
  POOL_PENDING: 'POOL_PENDING',
  ASSIGNED: 'ASSIGNED',
  ACCEPTED: 'ACCEPTED',
  DELIVERING: 'DELIVERING',
  DELIVERED: 'DELIVERED',
  AWAITING_CALLBACK: 'AWAITING_CALLBACK',
  CARRIER_CONFIRMED: 'CARRIER_CONFIRMED',
  COMPLETED: 'COMPLETED',
  EXCEPTION: 'EXCEPTION',
  CANCELLED: 'CANCELLED',
  /* F-22/R9：第 12 码。后端 OrderStatus.REJECTED（Enums.cs:18）与 PENDING_ASSIGN→REJECTED 迁移边
     （OrderStatusTransitions.cs:49）早已存在，中控端驳回写入口（t141）落地后该码即会出现在列表里。 */
  REJECTED: 'REJECTED',
  /* 紧急需求20261029 需求1：第 13 码（后端 OrderStatus.CARD_FAILED，与交付提交页第五档「开卡失败」同源）。
     用户已拍板它是**终态**：不进改派重办，也不能被任何在途边再推走。 */
  CARD_FAILED: 'CARD_FAILED',
} as const;
export type OrderStatus = (typeof ORDER_STATUS)[keyof typeof ORDER_STATUS];

export const ORDER_STATUS_META: Record<OrderStatus, EnumMeta<OrderStatus>> = {
  PENDING_ASSIGN: { label: '待分配', tone: 'dan' },
  POOL_PENDING: { label: '公区待领', tone: 'warn' },
  ASSIGNED: { label: '已分配', tone: 'pri' },
  ACCEPTED: { label: '已接单', tone: 'pri' },
  DELIVERING: { label: '交付中', tone: 'pri' },
  DELIVERED: { label: '交付完成', tone: 'ok' },
  /* 反馈文档_09 第 4 条（A 方案：后端状态机与历史数据一律不动，只收敛展示层）：交付完成与待回调在业务上属同一阶段，
     两个状态重复 ⇒ 本端不再把它当一枚独立状态展示：标签并入「交付完成」、状态下拉不再单列
     （ORDER_STATUS_FILTER_OPTIONS）、详情时间线继续把两枚码落在同一枚阶段节点上（OrderDetailView.statusToPhase）。
     （「已驳回」后来也退出下拉，但那是它自己的反馈条号，与本条 A 方案无关，见 ORDER_STATUS_FILTER_EXCLUDED_CODES。）
     码值仍在册：列表行里历史单仍可能回这一枚码，labelOf 一律渲染成「交付完成」。 */
  AWAITING_CALLBACK: { label: '交付完成', tone: 'ok' },
  CARRIER_CONFIRMED: { label: '运营商确认', tone: 'ok' },
  COMPLETED: { label: '已完结', tone: 'ok' },
  EXCEPTION: { label: '异常', tone: 'dan' },
  CANCELLED: { label: '已取消', tone: 'mut' },
  REJECTED: { label: '已驳回', tone: 'dan' },
  /* 紧急需求20261029 需求1：开卡失败与「异常/已驳回」同为失败族 ⇒ 同一枚红色调，⛔ 另造色 */
  CARD_FAILED: { label: '开卡失败', tone: 'dan' },
};

/**
 * 展示层归并表（反馈文档_09 第 4 条 A 方案）：待回调 ⇒ 交付完成。
 * 只描述「怎么显示」，⛔ 不参与任何写面/状态机——码值、后端 OrderStatus、历史单与结算 BillStatus 一律不动。
 */
export const ORDER_STATUS_DISPLAY_MERGED: Partial<Record<OrderStatus, OrderStatus>> = {
  [ORDER_STATUS.AWAITING_CALLBACK]: ORDER_STATUS.DELIVERED,
};

/** 展示层不再单列的码（归并表键集；状态下拉与阶段都按归并后的口径铺） */
export const ORDER_STATUS_DISPLAY_MERGED_CODES: readonly OrderStatus[] = [
  ORDER_STATUS.AWAITING_CALLBACK,
];

/**
 * 不进状态筛选下拉的码（反馈 2026-10-21 第 5 条：「已驳回」从状态下拉里移除）。
 * ⛔ 只决定「能不能拿它当筛选项」：码值仍冻结在册、中文标签仍在（labelOf(ORDER_STATUS_META, 'REJECTED')
 * 仍回「已驳回」），「全部」页签里历史单回 REJECTED 时列表/详情照旧渲染中文 —— 删的只是下拉那一格。
 * ⛔ 也别把它和本文件下方的 ORDER_STATUS_WITHOUT_TAB（页签表外登记）混作一回事：那是页签口径。
 */
export const ORDER_STATUS_FILTER_EXCLUDED_CODES: readonly OrderStatus[] = [ORDER_STATUS.REJECTED];

/** 展示用归并：命中归并表走目标码，其余原样返回；未知码也原样返回（⛔ 抛错、⛔ 吞成空串）。 */
export function displayOrderStatusOf(status: OrderStatus | string): OrderStatus | string {
  return ORDER_STATUS_DISPLAY_MERGED[status as OrderStatus] ?? status;
}

/**
 * 状态筛选下拉的**唯一**选项来源：「全部」占位之后逐码铺。两处收敛，理由各不相同：
 *   ① 已并入交付完成的那枚不再单列（否则下拉里会出现两枚同名「交付完成」，选了也分不清是哪一枚）；
 *   ② 「已驳回」不再进下拉（反馈 2026-10-21 第 5 条：驳回是终态、单量极少，下拉里不再给它留一格）。
 *      ⛔ 只剔选项不删码：「全部」页签与列表行里回 REJECTED 的历史单仍显示「已驳回」（见 labelOf 消费点）。
 * 故 13 枚冻结码 ⇒ 下拉 11 枚（−1 归并、−1 已驳回；紧急需求20261029 需求1 加开卡失败后 12→13 码）。
 * 未知码防线在 labelOf：它回落原码，故这里只负责枚数与文案，不去猜码。
 */
export const ORDER_STATUS_FILTER_OPTIONS: ReadonlyArray<{ value: OrderStatus; label: string }> =
  (Object.keys(ORDER_STATUS_META) as OrderStatus[])
    .filter((code) => !ORDER_STATUS_DISPLAY_MERGED_CODES.includes(code))
    .filter((code) => !ORDER_STATUS_FILTER_EXCLUDED_CODES.includes(code))
    .map((code) => ({ value: code, label: ORDER_STATUS_META[code].label }));

export const ORDER_TYPE = { NEW_CARD: 'NEW_CARD', RENEW: 'RENEW', UPGRADE: 'UPGRADE' } as const;
export type OrderType = (typeof ORDER_TYPE)[keyof typeof ORDER_TYPE];

/** order_type 取值待 PRD 补全（当前以 V4.9 UI + 后端契约为准） */
export const ORDER_TYPE_META: Record<OrderType, EnumMeta<OrderType>> = {
  NEW_CARD: { label: '新办卡' },
  RENEW: { label: '续约' },
  UPGRADE: { label: '升级套餐' },
};

export const DELIVERY_METHOD = {
  ON_SITE: 'ON_SITE',
  EXPRESS: 'EXPRESS',
  SELF_PICKUP: 'SELF_PICKUP',
} as const;
export type DeliveryMethod = (typeof DELIVERY_METHOD)[keyof typeof DELIVERY_METHOD];

/** 冻结取值：后端文档 v1.5 §6.0-⑧（三值保留，明文字符串传输，不出现 int） */
export const DELIVERY_METHOD_META: Record<DeliveryMethod, EnumMeta<DeliveryMethod>> = {
  ON_SITE: { label: '上门交付' },
  EXPRESS: { label: '快递寄送' },
  SELF_PICKUP: { label: '自提' },
};

/**
 * §6.0-⑧：P0 只承诺 ON_SITE（上门）。EXPRESS / SELF_PICKUP 归 P1 —— 六步交付 SOP 与现场
 * 凭证规则均按上门场景设计，另两种缺 SOP 支撑。P0 表单须置灰/隐藏，但字段仍可提交存储。
 */
export const DELIVERY_METHOD_P0: readonly DeliveryMethod[] = [DELIVERY_METHOD.ON_SITE];

export function isDeliveryMethodP0(method: DeliveryMethod): boolean {
  return DELIVERY_METHOD_P0.includes(method);
}

/**
 * 订单中心状态页签（V4.9 页签族 + 全部 + 交付完成，聚合 §5 的 12 个码值里的 11 枚）。
 * 页签为固定定义，计数来自 §6.2-1 `GET /orders/status-counts` 的码值映射求和，
 * 因此码值缺失按 0 计，页签数量不随数据抖动。
 *
 * 2026-10-13（测试反馈_04 第 14 条）：原「已驳回」页签下线——驳回是终态、单量极少，
 * 单列一枚页签长期占位却不常点。已驳回单仍在「全部」页签里可见。页签由此 9 → 8 枚
 * （紧急需求20261029 需求1 再补「开卡失败」一格 ⇒ 现 9 枚），
 * 表外那 1 枚码值改由 ORDER_STATUS_WITHOUT_TAB 显式登记（见下）。
 * 2026-10-21（本轮测试反馈第 5 条）：状态下拉里也不再有「已驳回」——口径见 ORDER_STATUS_FILTER_EXCLUDED_CODES；
 * 此处页签口径不动（REJECTED 仍不进页签，仍由「全部」页签兜着）。
 */
export interface OrderStatusTab {
  key: string;
  label: string;
  statuses: OrderStatus[];
}

export const ORDER_STATUS_TABS: readonly OrderStatusTab[] = [
  { key: 'all', label: '全部', statuses: [] },
  { key: 'wait', label: '待分配', statuses: [ORDER_STATUS.PENDING_ASSIGN] },
  { key: 'pool', label: '公区待领', statuses: [ORDER_STATUS.POOL_PENDING] },
  { key: 'doing', label: '交付中', statuses: [ORDER_STATUS.ASSIGNED, ORDER_STATUS.ACCEPTED, ORDER_STATUS.DELIVERING] },
  /* 交付完成单列一枚（用户反馈第 6 条）：DELIVERED 的口径是「货已送到」，与「运营商确认」不是同一件事——
     本表原先把 DELIVERED 并进 carrier，于是同一枚页签超管按 10 单算、交付后台按
     AWAITING_CALLBACK+CARRIER_CONFIRMED 算 0 单，两块屏对不上账。此处与交付后台 enums:306 对齐，
     并给 DELIVERED 单开一格；两仓对照表＝coord/feedback-20261011/tab-status-mapping.md（本批唯一真源）。 */
  { key: 'delivered', label: '交付完成', statuses: [ORDER_STATUS.DELIVERED] },
  { key: 'carrier', label: '运营商确认', statuses: [ORDER_STATUS.AWAITING_CALLBACK, ORDER_STATUS.CARRIER_CONFIRMED] },
  { key: 'end', label: '已完结', statuses: [ORDER_STATUS.COMPLETED] },
  /* 紧急需求20261029 需求1：开卡失败单列一格。它是终态、且是「卡没开成」这一独立业务结果，
     ⛔ 并进「异常/取消」——那两枚讲的是交付过程异常与被撤单，与开卡失败不是一回事。 */
  { key: 'cardfailed', label: '开卡失败', statuses: [ORDER_STATUS.CARD_FAILED] },
  { key: 'abnormal', label: '异常/取消', statuses: [ORDER_STATUS.EXCEPTION, ORDER_STATUS.CANCELLED] },
];

/**
 * 不进页签的状态码（页签表**显式**留白的码值，当前恰为 REJECTED 一枚）。
 *
 * 单一来源的并集判据（orderFacts.assert）：页签并集 ＋ 本清单 必须逐码覆盖 ORDER_STATUS 全集
 * 且不重不漏 —— 新增第 13 枚码值时若既不进页签也不登记在此，那条 assert 先红。
 * ⛔ 别把本清单当成「随手再加一枚」的口子：每加一枚都要有与下线同等的业务理由与反馈条号。
 */
export const ORDER_STATUS_WITHOUT_TAB: readonly OrderStatus[] = [ORDER_STATUS.REJECTED];

/**
 * 页签键 → 该页签覆盖的状态码集（项负责 seq=2599 裁的单一来源：列表请求要按页签带筛选值，
 * 而「哪枚页签聚合哪几枚码值」只许由上面那本表回答，⛔ 在视图或 mock 里另立一份映射）。
 *
 * `'all'` 与未知键都回空数组 ⇒ 空集的含义是「不按状态筛」，怎么处理归调用方：列表请求据此
 * **不带** `statuses` 那枚键（「全部」页签要回全量，见 `OrderListView.vue` 的 fetcher）。
 * ⛔ 把空集发成 `statuses=`：那与「不传」在筛选器那侧是两本账，别拿一次巧合当口径。
 */
export function statusesOfOrderTab(key?: string | null): OrderStatus[] {
  return ORDER_STATUS_TABS.find((tab) => tab.key === key)?.statuses ?? [];
}

/**
 * 运营商全站只有**一套**枚举（反馈_10 用户口径 m01861「运营商的枚举应该共用一套，不要这一套那一套」）：
 * 就是下面这枚 PRODUCT_CARRIER 四值。订单承运商曾经另立三值字典（TELECOM/UNICOM/MOBILE），本批已删除——
 * 提交面一律发 CHINA_*，读面遇到历史旧值只在下面那张兼容表里折一次。
 */

/**
 * **产品**所属运营商（四枚）——与后端 `md_product.carrier` 的取值表**逐字一致**
 * （2026-10-11「产品直接维护」批，后端 t17）。
 *
 * 全站唯一一套运营商枚举（产品、渠道、订单承运商、运营商工号都读这一枚；2026-10-17 起订单侧也不再另立字典）。
 * 四枚齐备，多一枚广电（`CHINA_BROADNET`）。⛔ 别把本枚折叠成三枚（折叠＝广电无处可落，后端 `Enum.IsDefined` 直接 422）。
 * ⛔ 也⛔ 由视图另写一份中文名——标签只许取自下面这张表（后端同一枚映射表给 `carrierLabel`）。
 */
export const PRODUCT_CARRIER = {
  CHINA_TELECOM: 'CHINA_TELECOM',
  CHINA_MOBILE: 'CHINA_MOBILE',
  CHINA_UNICOM: 'CHINA_UNICOM',
  CHINA_BROADNET: 'CHINA_BROADNET',
} as const;
export type ProductCarrier = (typeof PRODUCT_CARRIER)[keyof typeof PRODUCT_CARRIER];

export const PRODUCT_CARRIER_META: Record<ProductCarrier, EnumMeta<ProductCarrier>> = {
  CHINA_TELECOM: { label: '中国电信', code: PRODUCT_CARRIER.CHINA_TELECOM },
  CHINA_MOBILE: { label: '中国移动', code: PRODUCT_CARRIER.CHINA_MOBILE },
  CHINA_UNICOM: { label: '中国联通', code: PRODUCT_CARRIER.CHINA_UNICOM },
  CHINA_BROADNET: { label: '中国广电', code: PRODUCT_CARRIER.CHINA_BROADNET },
};

/** 下拉选项的唯一出口（顺序＝上表声明序，与后端枚举声明序同形） */
export const PRODUCT_CARRIER_OPTIONS: readonly { value: ProductCarrier; label: string }[] = (
  Object.values(PRODUCT_CARRIER) as ProductCarrier[]
).map((value) => ({ value, label: PRODUCT_CARRIER_META[value].label }));

/**
 * **迁移期兼容**（⛔ 不是第二张业务字典，只是把历史码值折回 PRODUCT_CARRIER）：
 * 后端同批改成只认 PRODUCT_CARRIER 并兼容旧值，前端读面上旧订单／旧渠道仍可能回 `TELECOM/UNICOM/MOBILE`，
 * 这里折一次让标签照常显示中文；提交面⛔ 发旧值。后端回填完成后整块可删。
 */
export const LEGACY_CARRIER_ALIAS: Readonly<Record<string, ProductCarrier>> = {
  TELECOM: PRODUCT_CARRIER.CHINA_TELECOM,
  UNICOM: PRODUCT_CARRIER.CHINA_UNICOM,
  MOBILE: PRODUCT_CARRIER.CHINA_MOBILE,
};

/** 运营商码归一成 PRODUCT_CARRIER（认旧值）；认不出回 null ⇒ 视图落「—」，⛔ 猜一枚顶上 */
export function carrierCodeOf(code: string | null | undefined): ProductCarrier | null {
  const text = (code ?? '').trim().toUpperCase();
  if (text === '') return null;
  const alias = LEGACY_CARRIER_ALIAS[text];
  if (alias) return alias;
  return (Object.values(PRODUCT_CARRIER) as string[]).includes(text) ? (text as ProductCarrier) : null;
}

/** 运营商码 → 中文名（旧三值也认，迁移期兼容）；认不出落「—」 */
export function carrierLabelOf(code: string | null | undefined): string {
  const normalized = carrierCodeOf(code);
  return normalized ? PRODUCT_CARRIER_META[normalized].label : '—';
}

export const ORDER_EVENT_TYPE = {
  CREATED: 'CREATED',
  /* 紧急需求20261029 需求1：交付端提交「开卡失败」时后端落的订单事件
     （CoreModules.cs 提交口 OrderEventType.CARD_FAILED）。详情时间线要认得它，
     否则开卡失败单在时间线上只剩原始码、与状态徽标对不上。 */
  CARD_FAILED: 'CARD_FAILED',
  VALIDATED: 'VALIDATED',
  ASSIGNED: 'ASSIGNED',
  TO_POOL: 'TO_POOL',
  GRABBED: 'GRABBED',
  RECALLED: 'RECALLED',
  ACCEPTED: 'ACCEPTED',
  CALL_CONTACT: 'CALL_CONTACT',
  DELIVER_START: 'DELIVER_START',
  DELIVERED: 'DELIVERED',
  PROOF_UPLOADED: 'PROOF_UPLOADED',
  CARRIER_CALLBACK: 'CARRIER_CALLBACK',
  ACTIVATED: 'ACTIVATED',
  FIRST_RECHARGE: 'FIRST_RECHARGE',
  RETENTION_CHECK: 'RETENTION_CHECK',
  URGED: 'URGED',
  REASSIGNED: 'REASSIGNED',
  EXCEPTION: 'EXCEPTION',
  SECOND_FOLLOW: 'SECOND_FOLLOW',
  CANCELLED: 'CANCELLED',
} as const;
export type OrderEventType = (typeof ORDER_EVENT_TYPE)[keyof typeof ORDER_EVENT_TYPE];

export const ORDER_EVENT_META: Record<OrderEventType, EnumMeta<OrderEventType>> = {
  CREATED: { label: '订单录入' },
  VALIDATED: { label: '四项校验' },
  ASSIGNED: { label: '分配' },
  TO_POOL: { label: '放入公区' },
  GRABBED: { label: '抢单' },
  RECALLED: { label: '回收' },
  ACCEPTED: { label: '接单' },
  CALL_CONTACT: { label: '电话联系' },
  DELIVER_START: { label: '开始交付' },
  DELIVERED: { label: '交付完成' },
  PROOF_UPLOADED: { label: '凭证上传' },
  CARRIER_CALLBACK: { label: '运营商回调' },
  ACTIVATED: { label: '激活' },
  FIRST_RECHARGE: { label: '首充' },
  RETENTION_CHECK: { label: '留存巡检' },
  URGED: { label: '催办' },
  REASSIGNED: { label: '改派' },
  EXCEPTION: { label: '异常' },
  SECOND_FOLLOW: { label: '二次跟进' },
  CANCELLED: { label: '取消' },
  CARD_FAILED: { label: '开卡失败' },
};

export const PROOF_TYPE = {
  CARD_PHOTO: 'CARD_PHOTO',
  ACTIVATION_PAGE: 'ACTIVATION_PAGE',
  SCENE_PHOTO: 'SCENE_PHOTO',
  SIGNATURE: 'SIGNATURE',
} as const;
export type ProofType = (typeof PROOF_TYPE)[keyof typeof PROOF_TYPE];

export const PROOF_TYPE_META: Record<ProofType, EnumMeta<ProofType>> = {
  CARD_PHOTO: { label: '卡品照' },
  ACTIVATION_PAGE: { label: '激活页' },
  SCENE_PHOTO: { label: '现场照' },
  SIGNATURE: { label: '客户签名' },
};

/** 二次跟进结果（契约 x-enum-set: second_follow_result；容器内该子键可空，⛔ 用 false/空串顶替） */
export const SECOND_FOLLOW_RESULT = {
  RESOLVED: 'RESOLVED',
  STILL_PENDING: 'STILL_PENDING',
  UNREACHABLE: 'UNREACHABLE',
} as const;
export type SecondFollowResult = (typeof SECOND_FOLLOW_RESULT)[keyof typeof SECOND_FOLLOW_RESULT];

export const SECOND_FOLLOW_RESULT_META: Record<SecondFollowResult, EnumMeta<SecondFollowResult>> = {
  RESOLVED: { label: '已解决', tone: 'ok' },
  STILL_PENDING: { label: '仍未定', tone: 'warn' },
  UNREACHABLE: { label: '联系不上', tone: 'dan' },
};

/**
 * 交付完成凭证口径（2026-10-13 批次 C 用户裁定，后端 t1 同批）：交付员提交订单完成时凭证
 * **不做强制要求**——可上传凭证，也可以不上传凭证 ⇒ 原「交付完成规则：≥3 类凭证 + 签名」常量
 * `PROOF_MIN_KINDS = 3` 已随该裁定整枚移除。⛔ 不要以任何形式把它加回来：本端对凭证只做
 * 「已上传几类／签名在不在」的事实展示，不存在合格／不合格判定，也不存在按凭证拦完成的前端闸。
 */

export const REJECT_REASON = {
  INCOMPLETE_INFO: 'INCOMPLETE_INFO',
  DUPLICATE_ENTRY: 'DUPLICATE_ENTRY',
  BLACKLIST: 'BLACKLIST',
  INVALID_NUMBER: 'INVALID_NUMBER',
  OTHER: 'OTHER',
} as const;
export type RejectReason = (typeof REJECT_REASON)[keyof typeof REJECT_REASON];

export const REJECT_REASON_META: Record<RejectReason, EnumMeta<RejectReason>> = {
  INCOMPLETE_INFO: { label: '信息不全', tone: 'warn' },
  DUPLICATE_ENTRY: { label: '重复录入', tone: 'dan' },
  BLACKLIST: { label: '命中黑名单', tone: 'dan' },
  INVALID_NUMBER: { label: '号码无效', tone: 'dan' },
  OTHER: { label: '其他', tone: 'mut' },
};

export const APPEAL_STATUS = {
  NONE: 'NONE',
  PENDING_REEDIT: 'PENDING_REEDIT',
  APPEALING: 'APPEALING',
  UPHELD: 'UPHELD',
  REJECTED: 'REJECTED',
} as const;
export type AppealStatus = (typeof APPEAL_STATUS)[keyof typeof APPEAL_STATUS];

export const APPEAL_STATUS_META: Record<AppealStatus, EnumMeta<AppealStatus>> = {
  NONE: { label: '无', tone: 'mut' },
  PENDING_REEDIT: { label: '待重编辑', tone: 'warn' },
  APPEALING: { label: '申诉中', tone: 'pri' },
  UPHELD: { label: '申诉成立', tone: 'ok' },
  REJECTED: { label: '申诉驳回', tone: 'dan' },
};

export const ALERT_LEVEL = { HIGH: 'HIGH', MEDIUM: 'MEDIUM', LOW: 'LOW' } as const;
export type AlertLevel = (typeof ALERT_LEVEL)[keyof typeof ALERT_LEVEL];

export const ALERT_LEVEL_META: Record<AlertLevel, EnumMeta<AlertLevel>> = {
  HIGH: { label: '高', tone: 'dan' },
  MEDIUM: { label: '中', tone: 'warn' },
  LOW: { label: '低', tone: 'pri' },
};

export const ALERT_TICKET_STATUS = {
  PENDING_CLAIM: 'PENDING_CLAIM',
  PROCESSING: 'PROCESSING',
  CLOSED: 'CLOSED',
  ESCALATED: 'ESCALATED',
} as const;
export type AlertTicketStatus = (typeof ALERT_TICKET_STATUS)[keyof typeof ALERT_TICKET_STATUS];

export const ALERT_TICKET_STATUS_META: Record<AlertTicketStatus, EnumMeta<AlertTicketStatus>> = {
  PENDING_CLAIM: { label: '待认领', tone: 'warn' },
  PROCESSING: { label: '处理中', tone: 'pri' },
  CLOSED: { label: '已关闭', tone: 'ok' },
  ESCALATED: { label: '已升级', tone: 'dan' },
};

/* ─── 告警规则触发条件词表（展示层；判定与上送一律不动） ───
 * 真源＝code-api/src/DeliveryHub.Modules/AlertDsl.cs：KnownMetrics（:22-26，八项）、KnownOperators（:29，七项）、
 * MetricCn（:304-315，中文名与单位）。前端只把码翻成中文业务措辞：⛔ 不参与命中判定、⛔ 不改写 triggerDsl、
 * ⛔ 不把未识别的码猜成相近项（宁可显式标「未知」并把原码带出来）。
 */
export const ALERT_METRIC = {
  CONV_RATE: 'conv_rate',
  POOL_STAGNANT_HOURS: 'pool_stagnant_hours',
  POOL_STAGNANT_COUNT: 'pool_stagnant_count',
  SLA_REMAINING_MINUTES: 'sla_remaining_minutes',
  P95_CALLBACK_HOURS: 'p95_callback_hours',
  MATCH_FAIL_RATE: 'match_fail_rate',
  DAILY_REJECT_RATE: 'daily_reject_rate',
  AVG_CLOSE_TIME: 'avg_close_time',
} as const;
export type AlertMetric = (typeof ALERT_METRIC)[keyof typeof ALERT_METRIC];

/** 指标中文名与单位后缀（逐字对齐 AlertDsl.MetricCn，⛔ 不自造更顺口的说法）。 */
export const ALERT_METRIC_META: Record<AlertMetric, { label: string; unit: string }> = {
  [ALERT_METRIC.CONV_RATE]: { label: '转化率', unit: '' },
  [ALERT_METRIC.POOL_STAGNANT_HOURS]: { label: '公区滞留时长', unit: 'h' },
  [ALERT_METRIC.POOL_STAGNANT_COUNT]: { label: '公区滞留单数', unit: ' 单' },
  [ALERT_METRIC.SLA_REMAINING_MINUTES]: { label: 'SLA 剩余时限', unit: 'min' },
  [ALERT_METRIC.P95_CALLBACK_HOURS]: { label: '回调 P95 耗时', unit: 'h' },
  [ALERT_METRIC.MATCH_FAIL_RATE]: { label: '回调匹配失败率', unit: '' },
  [ALERT_METRIC.DAILY_REJECT_RATE]: { label: '日驳回率', unit: '' },
  [ALERT_METRIC.AVG_CLOSE_TIME]: { label: '平均关闭时长', unit: 'h' },
};

export const ALERT_OPERATOR = {
  GT: 'gt',
  LT: 'lt',
  GTE: 'gte',
  LTE: 'lte',
  EQ: 'eq',
  DEVIATION_GT: 'deviation_gt',
  DEVIATION_LT: 'deviation_lt',
} as const;
export type AlertOperator = (typeof ALERT_OPERATOR)[keyof typeof ALERT_OPERATOR];

/** 算子中文（后端无中文表，本表由前端立）：baseline=true 的两个是偏离类，额外带基线偏离比例。 */
export const ALERT_OPERATOR_META: Record<AlertOperator, { label: string; baseline: boolean }> = {
  [ALERT_OPERATOR.GT]: { label: '大于', baseline: false },
  [ALERT_OPERATOR.LT]: { label: '小于', baseline: false },
  [ALERT_OPERATOR.GTE]: { label: '大于等于', baseline: false },
  [ALERT_OPERATOR.LTE]: { label: '小于等于', baseline: false },
  [ALERT_OPERATOR.EQ]: { label: '等于', baseline: false },
  [ALERT_OPERATOR.DEVIATION_GT]: { label: '高于基线', baseline: true },
  [ALERT_OPERATOR.DEVIATION_LT]: { label: '低于基线', baseline: true },
};

/** 触发条件展示读的几格（AlertRule 的结构子集；⛔ 不 import 接口/视图层，enums 保持零依赖）。 */
export interface AlertConditionFacts {
  metric?: string | null;
  operator?: string | null;
  threshold?: string | null;
  window?: string | null;
  baselineDeviation?: string | null;
  triggerDsl?: string | null;
}

function condCode(raw: unknown): string {
  return typeof raw === 'string' ? raw.trim() : '';
}

/** 指标码 ⇒ 中文名；空⇒未配置，未识别⇒「未知指标（原码）」，⛔ 不留白、不猜成相近项。
 *  单位不并进这里：后端 MetricCn 的成品措辞是「公区滞留时长 52h（阈值 48h）」，单位跟在数值后面（见下方 alertConditionText）。 */
export function alertMetricText(metric: unknown): string {
  const code = condCode(metric);
  if (!code) return '指标未配置';
  const hit: { label: string; unit: string } | undefined = ALERT_METRIC_META[code as AlertMetric];
  return hit ? hit.label : '未知指标（' + code + '）';
}

/** 算子码 ⇒ 中文；空⇒未配置，未识别⇒「未知算子（原码）」。 */
export function alertOperatorText(operator: unknown): string {
  const code = condCode(operator);
  if (!code) return '算子未配置';
  const hit: { label: string; baseline: boolean } | undefined = ALERT_OPERATOR_META[code as AlertOperator];
  return hit ? hit.label : '未知算子（' + code + '）';
}

/** 基线偏离比例：DSL 存比值（种子 baseline_deviation=0.20 ⇒ 20%）；非有限数原样带出，⛔ 不拿 0 顶。 */
function deviationPercentText(raw: unknown): string {
  const text = condCode(raw);
  if (!text) return '';
  const num = Number(text);
  if (!Number.isFinite(num)) return text;
  return String(Number((num * 100).toFixed(4))) + '%';
}

/**
 * 触发条件的中文业务描述（规则列表默认显示的就是这一串）：
 * 非偏离类 ⇒ 公区滞留时长 大于 48h（窗口 24h）；偏离类 ⇒ 转化率 低于基线 20%（阈值 0.85，窗口 7d）。
 * 缺项不空白：阈值/窗口缺席写「阈值未配置／窗口未配置」，指标/算子缺席写「指标未配置／算子未配置」。
 */
export function alertConditionText(rule: AlertConditionFacts | null | undefined): string {
  const facts: AlertConditionFacts = rule ?? {};
  const metric = alertMetricText(facts.metric);
  const operator = alertOperatorText(facts.operator);
  const unit: string = ALERT_METRIC_META[condCode(facts.metric) as AlertMetric]?.unit ?? '';
  const thresholdRaw = condCode(facts.threshold);
  const thresholdText = thresholdRaw ? thresholdRaw + unit : '阈值未配置';
  const windowRaw = condCode(facts.window);
  const windowText = windowRaw ? '窗口 ' + windowRaw : '窗口未配置';
  const opCode = condCode(facts.operator);
  const pct = ALERT_OPERATOR_META[opCode as AlertOperator]?.baseline ? deviationPercentText(facts.baselineDeviation) : '';
  if (pct) return metric + ' ' + operator + ' ' + pct + '（阈值 ' + thresholdText + '，' + windowText + '）';
  return metric + ' ' + operator + ' ' + thresholdText + '（' + windowText + '）';
}

/** 原始 DSL 文本（列表那格的 title）：后端原串优先，缺席时按已解析字段重建，⛔ 不给空 title。 */
export function alertDslText(rule: AlertConditionFacts | null | undefined): string {
  const facts: AlertConditionFacts = rule ?? {};
  const raw = condCode(facts.triggerDsl);
  if (raw) return raw;
  return (
    'metric=' + condCode(facts.metric)
    + ' operator=' + condCode(facts.operator)
    + ' threshold=' + condCode(facts.threshold)
    + ' window=' + condCode(facts.window)
  );
}

/**
 * 订单详情「操作日志」的动作码 —— 只收后端真正会写进订单审计的码，一枚不多。
 *
 * 码源：真源 code-api 的 `src/DeliveryHub.Domain/Enums/Enums.cs:101-119` AuditAction 声明序
 * （与冻结契约 `tests/DeliveryHub.Tests/ContractOrderDetail.g.cs:171` 的 audit_action 枚举逐字同序），
 * 落库 `sys_audit_log.action` varchar(32) 明文。
 * 写腿（本面板要吃的那几枚）：`src/DeliveryHub.Modules/AdminOrderWrite.cs:580-585`
 * （admin.order.assign/cancel/reassign/recall/to-pool/urge）、
 * `OrderDetailExportDashboardModule.cs:512/516` ORDER_NOTE、:540 ORDER_REJECT、
 * :600 ORDER_BATCH（targetType=batch）、:634/:679/:728 导出三枚。
 *
 * 2026-10-13 按订单面测试反馈补齐姓名、同时下线两枚过期码（⛔ 不复活）：
 * - 补 ORDER_RECALL / ORDER_REASSIGN / ORDER_TO_POOL / ORDER_REJECT / ORDER_NOTE / ORDER_BATCH
 *   六枚：后端驳回腿、批量腿、收回/改派/入公区腿一直在写，面板原先只能显示英文原码。
 * - 删 RULE_CHANGE：后端已删。Enums.cs:80-83 裁定 D 把它拆成 ALERT_RULE_CHANGE 与
 *   SETTLE_RULE_CHANGE 两枚具体动作，Step4Item3AuditActionCodebookTests
 *   「裁定D_笼统RULE_CHANGE已删_由两枚具体规则动作取代」钉死 `"RULE_CHANGE".TryToEnum<AuditAction>()`
 *   为 false。⛔ 两枚拆分码不是它的别名，一枚动作不许两枚码。
 * - 删 ORDER_CREATE：后端码册与契约里都没有这枚（code-api 全仓 0 命中、无写腿），
 *   历史离线夹具里的 ORDER_CREATE 行不构成在线码。删后它落回 `labelOf` 的原码兜底。
 *
 * ⛔ 不要凭文案猜码：新增键前先在后端码册与写腿里核到同名字符串。其余契约码
 * （告警/结算/工单/账号/导出下载与申诉等）不属订单操作日志这一格，本表不收。
 */
export const AUDIT_ACTION = {
  LOGIN: 'LOGIN',
  LOGOUT: 'LOGOUT',
  ORDER_ASSIGN: 'ORDER_ASSIGN',
  ORDER_RECALL: 'ORDER_RECALL',
  ORDER_REASSIGN: 'ORDER_REASSIGN',
  ORDER_TO_POOL: 'ORDER_TO_POOL',
  ORDER_URGE: 'ORDER_URGE',
  ORDER_REJECT: 'ORDER_REJECT',
  ORDER_CANCEL: 'ORDER_CANCEL',
  ORDER_NOTE: 'ORDER_NOTE',
  ORDER_BATCH: 'ORDER_BATCH',
  EXPORT_REQUEST: 'EXPORT_REQUEST',
  EXPORT_APPROVED: 'EXPORT_APPROVED',
  ROLE_PERM_CHANGE: 'ROLE_PERM_CHANGE',
  CONFIG_CHANGE: 'CONFIG_CHANGE',
  COURIER_AUDIT: 'COURIER_AUDIT',
} as const;
export type AuditAction = (typeof AUDIT_ACTION)[keyof typeof AUDIT_ACTION];

export const AUDIT_ACTION_META: Record<AuditAction, EnumMeta<AuditAction>> = {
  LOGIN: { label: '登录' },
  LOGOUT: { label: '登出' },
  ORDER_ASSIGN: { label: '订单分配' },
  ORDER_RECALL: { label: '订单收回' },
  ORDER_REASSIGN: { label: '订单改派' },
  ORDER_TO_POOL: { label: '订单入公区' },
  ORDER_URGE: { label: '订单催办' },
  ORDER_REJECT: { label: '订单驳回' },
  ORDER_CANCEL: { label: '订单取消' },
  ORDER_NOTE: { label: '订单备注' },
  ORDER_BATCH: { label: '批量操作' },
  EXPORT_REQUEST: { label: '导出申请' },
  EXPORT_APPROVED: { label: '导出审批' },
  ROLE_PERM_CHANGE: { label: '权限变更' },
  CONFIG_CHANGE: { label: '配置变更' },
  COURIER_AUDIT: { label: '专员审核' },
};

/**
 * 订单流转审计码册（唯一口径源：coord/feedback-20261030/req02/contract.md §5.2／§5.4）。
 *
 * 这批码由订单状态机的写口直接落审计面，属**模块级码册**（后端 OrderFlowAuditActions），
 * 不在 AUDIT_ACTION 枚举里 —— 因此 ⛔ 塞进 AUDIT_ACTION_META：那张表与码册同集
 * （orderFacts.assert.ts 钉住「键集相等」），混进去等于伪造影射关系。
 * 本表只做「码 → 中文名」展示映射，码本身的登记与写入责任在后端；后端码册改名时这里要跟着改，
 * 未收录码由 orderFlowActionLabelOf 兜成中性中文，⛔ 裸露原码当唯一展示。
 */
/* 逐枚取自后端模块级码册 OrderFlowAuditActions 的 "ORDER_..." 字面量常量（唯一真源），
   中文名沿用后端 LabelOf 的口径 —— 同一件动作在两个端显示成两个名字，比显示原码更让人怀疑数据。
   码册新增一枚而这里没跟上时，orderFacts 的机械对账会点名判红（⛔ 靠人记清单）。 */
export const ORDER_FLOW_AUDIT_ACTION_META: Record<string, EnumMeta<string>> = {
  ORDER_CREATED: { label: '录单' },
  ORDER_GRABBED: { label: '领取' },
  ORDER_ACCEPTED: { label: '接单' },
  ORDER_DELIVER_START: { label: '开始交付' },
  ORDER_DELIVER_SUBMIT: { label: '提交交付' },
  ORDER_RESCHEDULED: { label: '预约改期' },
  ORDER_CARD_FAILED: { label: '开卡失败' },
  ORDER_USER_REFUSED: { label: '用户拒绝' },
  ORDER_EXCEPTION: { label: '转异常' },
  ORDER_RECALL_ESCALATE: { label: '公区回收升级' },
  ORDER_UNREACHABLE_RECALL: { label: '联系不上用户，回收至后台' },
  ORDER_END: { label: '结束订单' },
  ORDER_MANUAL_REASSIGN: { label: '人工改派' },
  ORDER_MANUAL_CANCEL: { label: '人工取消' },
  ORDER_AUTO_RECALL: { label: '超时自动回收' },
  ORDER_CARRIER_SUBMIT: { label: '上报运营商' },
  ORDER_CARRIER_SUBMIT_FAILED: { label: '上报运营商失败' },
  ORDER_CARRIER_CALLBACK: { label: '运营商回调' },
  ORDER_CALLBACK_TIMEOUT: { label: '回调超时' },
  ORDER_ACTIVATED: { label: '激活完成' },
  ORDER_POST_CHECK_FAILED: { label: '事后核验失败' },
  ORDER_REASSIGN_AFTER_CANCEL: { label: '取消后改派' },
  ORDER_REASSIGN_FROM_EXCEPTION: { label: '异常单改派' },
};

/** 该动作码在（在册码册 ∪ 订单流转码册）里有没有中文名：视图据此决定要不要把原码放进悬停说明。 */
export function orderFlowActionHasLabel(action: string | null | undefined): boolean {
  if (!action) return false;
  return Boolean(AUDIT_ACTION_META[action as AuditAction] ?? ORDER_FLOW_AUDIT_ACTION_META[action]);
}

/**
 * 订单审计动作的中文名：先查在册码册 AUDIT_ACTION_META，再查订单流转码册；两处都没有 ⇒ 中性中文。
 * ⛔ 回落原码：那是后端编码，直接上屏等于让人读实现细节（与 devtextResidue 同一条纪律）。
 */
export function orderFlowActionLabelOf(action: string | null | undefined, fallback = '其它操作'): string {
  if (!action) return fallback;
  return (AUDIT_ACTION_META[action as AuditAction] ?? ORDER_FLOW_AUDIT_ACTION_META[action])?.label ?? fallback;
}

/**
 * 操作日志动作列的中文名：**后端随行下发的 actionText 优先**（t15 起单一真源在后端 LabelOf），
 * 只有它缺失／空串／纯空白时才回落本地合并件，仍不命中给中性中文。
 *
 * ⛔ 本地表优先：后端改措辞后本端还显示旧名，同一动作两端两个叫法（7 枚复用码正是这样漂开的）；
 * ⛔ 把空串当有效值：动作列会渲染成一片空白格；
 * ⛔ 回落原码：那是后端编码，与 devtextResidue 同一条纪律。
 */
export function orderFlowLogLabelOf(
  action: string | null | undefined,
  actionText: string | null | undefined,
  fallback = '其它操作',
): string {
  const served = typeof actionText === 'string' ? actionText.trim() : '';
  return served || orderFlowActionLabelOf(action, fallback);
}

/** 历史离线夹具兼容码，不是在线区域目录或授权全集。在线选项仅来自主数据。 */
export const REGION_CODES = ['WUHOU', 'GAOXIN', 'JINJIANG', 'CHENGHUA', 'SHUANGLIU'] as const;
export type RegionCode = string;

/** 区域是服务端业务码，不归一大小写、不从数字或对象补造码。 */
export function normalizeRegionCodes(values: unknown): RegionCode[] {
  return Array.isArray(values)
    ? [...new Set(values.filter((code): code is string => typeof code === 'string').map(code => code.trim()).filter(Boolean))]
    : [];
}

/** 仅真实名称优先；缺名显示原码，绝不查询历史五区表猜名。 */
export function regionFactText(code: unknown, name?: unknown, empty = '—'): string {
  if (typeof name === 'string' && name.trim()) return name.trim();
  return typeof code === 'string' && code.trim() ? code.trim() : empty;
}

/** ALL 只能由明确 scope 事实给出；码的数量与目录长度均不是全集证据。 */
export function regionCodesText(codes: unknown, names: readonly { code: string; name: string }[] = [], scope?: unknown): string {
  if (scope === DATA_SCOPE.ALL) return '全部';
  return normalizeRegionCodes(codes).map(code => regionFactText(code, names.find(item => item.code === code)?.name)).join(' / ') || '—';
}

export const REGION_META: Record<RegionCode, { label: string }> = {
  WUHOU: { label: '武侯区' },
  GAOXIN: { label: '高新区' },
  JINJIANG: { label: '锦江区' },
  CHENGHUA: { label: '成华区' },
  SHUANGLIU: { label: '双流区' },
};

/**
 * BPO 机构（`bpo_agency`，需求文档 v1.4 §4.2）。库面是 `smallint DEFAULT 1`，⛔ 改成字符串枚举
 * 去迁就前端 —— 这里的两枚值就是线上传输值，桩与视图共用同一份定义。
 */
export const AGENCY_STATUS = { ENABLED: 1, DISABLED: 0 } as const;
export type AgencyStatus = (typeof AGENCY_STATUS)[keyof typeof AGENCY_STATUS];

export const AGENCY_STATUS_LABEL: Record<AgencyStatus, string> = {
  [AGENCY_STATUS.ENABLED]: '启用',
  [AGENCY_STATUS.DISABLED]: '停用',
};

export const AGENCY_STATUS_TONE: Record<AgencyStatus, Tone> = {
  [AGENCY_STATUS.ENABLED]: 'ok',
  [AGENCY_STATUS.DISABLED]: 'dan',
};

/** 成员在机构内的角色（`bpo_agency_member.agency_role`，varchar(32)，默认 AGENT） */
export const AGENCY_ROLE = { ADMIN: 'ADMIN', AGENT: 'AGENT' } as const;
export type AgencyRole = (typeof AGENCY_ROLE)[keyof typeof AGENCY_ROLE];

export const AGENCY_ROLE_META: Record<AgencyRole, EnumMeta<AgencyRole>> = {
  ADMIN: { label: '机构管理员', tone: 'pri' },
  AGENT: { label: '录单员', tone: 'mut' },
};

/**
 * 机构管理七枚操作码（v1.4 §2.6）——**唯一一份定义**：权限树节点、角色配置矩阵、路由 `meta.perm`
 * 与 `v-perm` 都从这里取，⛔ 在任何一处再抄一遍字符串（抄两遍就是两本账，改一漏一）。
 * 码形两段小写；`agency:orders:all` 系群主 seq=809 批准的三段首例，⛔ 顺手改回两段。
 */
export const AGENCY_PERMISSION = {
  VIEW: 'agency:view',
  CREATE: 'agency:create',
  EDIT: 'agency:edit',
  DISABLE: 'agency:disable',
  STAFF: 'agency:staff',
  ORDERS_ALL: 'agency:orders:all',
  EXPORT: 'agency:export',
} as const;
export type AgencyPermissionCode = (typeof AGENCY_PERMISSION)[keyof typeof AGENCY_PERMISSION];

export const AGENCY_PERMISSION_META: Record<AgencyPermissionCode, EnumMeta<AgencyPermissionCode>> = {
  [AGENCY_PERMISSION.VIEW]: { label: '查看机构列表 / 详情' },
  [AGENCY_PERMISSION.CREATE]: { label: '新建机构' },
  [AGENCY_PERMISSION.EDIT]: { label: '编辑机构' },
  [AGENCY_PERMISSION.DISABLE]: { label: '停用 / 启用机构' },
  [AGENCY_PERMISSION.STAFF]: { label: '管理机构员工' },
  [AGENCY_PERMISSION.ORDERS_ALL]: { label: '查看机构全量订单' },
  [AGENCY_PERMISSION.EXPORT]: { label: '导出机构订单数据' },
};

/** 超管侧四枚（落 OPS_ADMIN）与 BPO 侧三枚（落 BPO_ADMIN）的分组，角色配置矩阵按此分列 */
export const AGENCY_ADMIN_SIDE_CODES: readonly AgencyPermissionCode[] = [
  AGENCY_PERMISSION.VIEW,
  AGENCY_PERMISSION.CREATE,
  AGENCY_PERMISSION.EDIT,
  AGENCY_PERMISSION.DISABLE,
];
export const AGENCY_BPO_SIDE_CODES: readonly AgencyPermissionCode[] = [
  AGENCY_PERMISSION.ORDERS_ALL,
  AGENCY_PERMISSION.STAFF,
  AGENCY_PERMISSION.EXPORT,
];

/** 机构管理面的审计动作码（v1.4 §4.1「操作审计」页签；库面存码，中文名在这里映射） */
export const AGENCY_AUDIT_ACTION = {
  CREATE: 'agency:create',
  EDIT: 'agency:edit',
  DISABLE: 'agency:disable',
  ENABLE: 'agency:enable',
  STAFF_CREATE: 'agency:staff:create',
  STAFF_DISABLE: 'agency:staff:disable',
  ORDERS_VIEW: 'agency:orders:view',
  EXPORT: 'agency:export',
} as const;
export type AgencyAuditAction = (typeof AGENCY_AUDIT_ACTION)[keyof typeof AGENCY_AUDIT_ACTION];

export const AGENCY_AUDIT_ACTION_META: Record<AgencyAuditAction, EnumMeta<AgencyAuditAction>> = {
  [AGENCY_AUDIT_ACTION.CREATE]: { label: '新建机构' },
  [AGENCY_AUDIT_ACTION.EDIT]: { label: '编辑机构' },
  [AGENCY_AUDIT_ACTION.DISABLE]: { label: '停用机构' },
  [AGENCY_AUDIT_ACTION.ENABLE]: { label: '启用机构' },
  [AGENCY_AUDIT_ACTION.STAFF_CREATE]: { label: '新建成员' },
  [AGENCY_AUDIT_ACTION.STAFF_DISABLE]: { label: '停用成员' },
  [AGENCY_AUDIT_ACTION.ORDERS_VIEW]: { label: '查看机构订单' },
  [AGENCY_AUDIT_ACTION.EXPORT]: { label: '导出机构订单' },
};

export function labelOf<T extends string>(
  meta: Record<T, EnumMeta<T>>,
  value: T | string | null | undefined,
  fallback = '—',
): string {
  if (!value) return fallback;
  const hit = meta[value as T];
  return hit ? hit.label : String(value);
}

/**
 * 外部响应枚举值归一：§6.0-③ 约定线上一律 `UPPER_SNAKE_CASE` 明文，但文档描述性写法
 * （如 §6.2-7 `endBinding` 写作 `admin` / `bpo` / `miniapp`）与真实序列化可能大小写不一致。
 * 按大写归一后在**封闭集**内匹配，未命中回落 `fallback` —— 不猜测、不静默转成相近值。
 */
export function normalizeEnum<T extends string>(raw: unknown, allowed: readonly T[], fallback: T): T {
  return normalizeEnumOrNull(raw, allowed) ?? fallback;
}

/** 同上，但未命中返回 `null`（用于可空字段，避免把未知值伪造成合法值） */
export function normalizeEnumOrNull<T extends string>(raw: unknown, allowed: readonly T[]): T | null {
  if (raw === null || raw === undefined) return null;
  const exact = allowed.find((code) => code === raw);
  if (exact) return exact;
  const upper = String(raw).trim().toUpperCase();
  return allowed.find((code) => code === upper) ?? null;
}

export function toneOf<T extends string>(
  meta: Record<T, EnumMeta<T>>,
  value: T | string | null | undefined,
): Tone | undefined {
  if (!value) return undefined;
  return meta[value as T]?.tone;
}

/** 枚举 → 下拉选项 */
export function optionsOf<T extends string>(
  meta: Record<T, EnumMeta<T>>,
): Array<{ value: T; label: string }> {
  return (Object.keys(meta) as T[]).map((key) => ({ value: key, label: meta[key].label }));
}

/* ---------------- 交付机构管理（契约 coord/delivery-agency/CONTRACT-delivery-agency.md §1.2/§1.5/§3.1） ---------------- */

/**
 * 成员在交付机构内的角色（`biz_delivery_agency_member.member_role`，varchar(32)，默认 `COURIER`；契约 §1.2）。
 * ⛔ 复刻 BPO 的 `AGENCY_ROLE`：那边列名是 `agency_role`、默认值是 `AGENT`，两套取值域分开定义，
 * 免得「录单员」与「交付员」共用一枚标签、库里却是两张脸。
 */
export const DELIVERY_MEMBER_ROLE = { AGENCY_ADMIN: "AGENCY_ADMIN", COURIER: "COURIER" } as const;
export type DeliveryMemberRole = (typeof DELIVERY_MEMBER_ROLE)[keyof typeof DELIVERY_MEMBER_ROLE];

export const DELIVERY_MEMBER_ROLE_META: Record<DeliveryMemberRole, EnumMeta<DeliveryMemberRole>> = {
  AGENCY_ADMIN: { label: "交付机构管理员", tone: "pri" },
  COURIER: { label: "交付员", tone: "mut" },
};

/**
 * 分组可见性开关（节点码，契约 §3.1）：角色 `permissions` 里必须**显式**含 MODULE 码＋至少一枚 PAGE 码，
 * 否则「交付机构管理」整块不渲染（`AdminLayout.vue:49-84` 的判据）。
 */
export const DELIVERY_AGENCY_NODE = {
  MODULE: "delivery_agency",
  LIST: "delivery_agency_list",
  DETAIL: "delivery_agency_detail",
  ROLES: "delivery_agency_roles",
} as const;
export type DeliveryAgencyNodeCode = (typeof DELIVERY_AGENCY_NODE)[keyof typeof DELIVERY_AGENCY_NODE];

/**
 * 交付机构动作码七枚（契约 §3.1）——**唯一一份定义**：权限树节点、角色配置矩阵、路由 `meta.perm` 与
 * `v-perm` 都从这里取，⛔ 在任何一处再抄一遍字符串。码形两段小写；`dagency:orders:all` 是与
 * `agency:orders:all` 同形的三段首例，⛔ 顺手改回两段。
 */
export const DELIVERY_AGENCY_PERMISSION = {
  VIEW: "dagency:view",
  CREATE: "dagency:create",
  EDIT: "dagency:edit",
  DISABLE: "dagency:disable",
  STAFF: "dagency:staff",
  ORDERS_ALL: "dagency:orders:all",
  EXPORT: "dagency:export",
} as const;
export type DeliveryAgencyPermissionCode =
  (typeof DELIVERY_AGENCY_PERMISSION)[keyof typeof DELIVERY_AGENCY_PERMISSION];

export const DELIVERY_AGENCY_PERMISSION_META: Record<
  DeliveryAgencyPermissionCode,
  EnumMeta<DeliveryAgencyPermissionCode>
> = {
  [DELIVERY_AGENCY_PERMISSION.VIEW]: { label: "查看交付机构列表 / 详情" },
  [DELIVERY_AGENCY_PERMISSION.CREATE]: { label: "新建交付机构" },
  [DELIVERY_AGENCY_PERMISSION.EDIT]: { label: "编辑交付机构" },
  [DELIVERY_AGENCY_PERMISSION.DISABLE]: { label: "停用 / 启用交付机构" },
  [DELIVERY_AGENCY_PERMISSION.STAFF]: { label: "管理交付机构成员" },
  [DELIVERY_AGENCY_PERMISSION.ORDERS_ALL]: { label: "查看交付机构全量订单" },
  [DELIVERY_AGENCY_PERMISSION.EXPORT]: { label: "导出交付机构订单数据" },
};

/** 平台侧四枚（落 OPS_ADMIN，全 11 枚）与机构侧三枚（裁定 R-1：交付机构管理员共授七枚）的分栏，矩阵按此两列排 */
export const DELIVERY_AGENCY_PLATFORM_SIDE_CODES: readonly DeliveryAgencyPermissionCode[] = [
  DELIVERY_AGENCY_PERMISSION.VIEW,
  DELIVERY_AGENCY_PERMISSION.CREATE,
  DELIVERY_AGENCY_PERMISSION.EDIT,
  DELIVERY_AGENCY_PERMISSION.DISABLE,
];
export const DELIVERY_AGENCY_AGENCY_SIDE_CODES: readonly DeliveryAgencyPermissionCode[] = [
  DELIVERY_AGENCY_PERMISSION.STAFF,
  DELIVERY_AGENCY_PERMISSION.ORDERS_ALL,
  DELIVERY_AGENCY_PERMISSION.EXPORT,
];

/** 角色配置屏可勾选面＝节点码 4 ＋ 动作码 7（共 11 枚；契约 §3.1）。矩阵行序即此序 */
export const DELIVERY_AGENCY_MANAGED_CODES: readonly string[] = [
  DELIVERY_AGENCY_NODE.MODULE,
  DELIVERY_AGENCY_NODE.LIST,
  DELIVERY_AGENCY_NODE.DETAIL,
  DELIVERY_AGENCY_NODE.ROLES,
  ...DELIVERY_AGENCY_PLATFORM_SIDE_CODES,
  ...DELIVERY_AGENCY_AGENCY_SIDE_CODES,
];

/**
 * 交付机构面的审计动作码六枚（契约 §1.5）。与 BPO 面不同，库面存**大写蛇形**码（前例：交付域新增码一律
 * UPPER_SNAKE），故 ⛔ 照 BPO 的 `agency:*` 小写点号形造一套；中文名在这里映射。
 */
export const DELIVERY_AGENCY_AUDIT_ACTION = {
  CREATE: "DELIVERY_AGENCY_CREATE",
  UPDATE: "DELIVERY_AGENCY_UPDATE",
  STATUS: "DELIVERY_AGENCY_STATUS",
  MEMBER_CREATE: "DELIVERY_AGENCY_MEMBER_CREATE",
  MEMBER_UPDATE: "DELIVERY_AGENCY_MEMBER_UPDATE",
  MEMBER_STATUS: "DELIVERY_AGENCY_MEMBER_STATUS",
} as const;
export type DeliveryAgencyAuditAction =
  (typeof DELIVERY_AGENCY_AUDIT_ACTION)[keyof typeof DELIVERY_AGENCY_AUDIT_ACTION];

export const DELIVERY_AGENCY_AUDIT_ACTION_META: Record<
  DeliveryAgencyAuditAction,
  EnumMeta<DeliveryAgencyAuditAction>
> = {
  [DELIVERY_AGENCY_AUDIT_ACTION.CREATE]: { label: "新建交付机构" },
  [DELIVERY_AGENCY_AUDIT_ACTION.UPDATE]: { label: "编辑交付机构" },
  [DELIVERY_AGENCY_AUDIT_ACTION.STATUS]: { label: "停用 / 启用交付机构" },
  [DELIVERY_AGENCY_AUDIT_ACTION.MEMBER_CREATE]: { label: "新建机构成员" },
  [DELIVERY_AGENCY_AUDIT_ACTION.MEMBER_UPDATE]: { label: "编辑机构成员" },
  [DELIVERY_AGENCY_AUDIT_ACTION.MEMBER_STATUS]: { label: "停用 / 启用机构成员" },
};

/** 节点码四枚（分组可见性与分组内页面开关；契约 §3.1）——与动作码分开成册，⛔ 混进同一枚 union */
export const DELIVERY_AGENCY_NODE_CODES: readonly DeliveryAgencyNodeCode[] = [
  DELIVERY_AGENCY_NODE.MODULE,
  DELIVERY_AGENCY_NODE.LIST,
  DELIVERY_AGENCY_NODE.DETAIL,
  DELIVERY_AGENCY_NODE.ROLES,
];

export const DELIVERY_AGENCY_NODE_META: Record<DeliveryAgencyNodeCode, EnumMeta<DeliveryAgencyNodeCode>> = {
  [DELIVERY_AGENCY_NODE.MODULE]: { label: "交付机构管理（分组）" },
  [DELIVERY_AGENCY_NODE.LIST]: { label: "交付机构列表" },
  [DELIVERY_AGENCY_NODE.DETAIL]: { label: "交付机构详情" },
  [DELIVERY_AGENCY_NODE.ROLES]: { label: "交付机构角色配置" },
};

/**
 * 码 → 中文名的**唯一出口**（节点码与动作码两册合并查）：矩阵里两族混排，⛔ 在视图里再写一张 if 表。
 * 两册都不命中的码原样回显（库面陌生码要能看见，⛔ 静默吞成 —）。
 */
export function deliveryCodeLabel(code: string): string {
  if (code in DELIVERY_AGENCY_PERMISSION_META) {
    return DELIVERY_AGENCY_PERMISSION_META[code as DeliveryAgencyPermissionCode].label;
  }
  if (code in DELIVERY_AGENCY_NODE_META) {
    return DELIVERY_AGENCY_NODE_META[code as DeliveryAgencyNodeCode].label;
  }
  return code;
}
